import * as React from 'react';
import { useAuth } from 'react-oidc-context';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
} from '@patternfly/react-core';
import { BuilderImageIcon, ExclamationCircleIcon, TachometerAltIcon } from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import { useClient } from '../../hooks/useClient';

const Dashboard: React.FunctionComponent = () => {
  const auth = useAuth();
  const { client, loading, error, refetch } = useClient();

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load dashboard"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error.message}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (!client) {
      return (
        <EmptyState titleText="Welcome to Ancalagon Manager" icon={BuilderImageIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            You need to fill in your Organization information before you can order products.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/organization/billing" />}>
                Go to Organization Settings
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <EmptyState titleText="Dashboard coming soon" icon={TachometerAltIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>
          Once available, this page will give you an at-a-glance view of your infrastructure: active virtual machines,
          running containers, vCPU and vRAM usage, and total storage allocated. In the meantime, you can manage your
          resources from the sections below.
        </EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/virtual-machines" />}>
              Go to Virtual Machines
            </Button>
            <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/emails" />}>
              Go to Emails
            </Button>
            <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/domains" />}>
              Go to Domain Names
            </Button>
            <Button variant="link" component={(props) => <Link {...props} to="/organization/billing" />}>
              Organization settings
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/" isActive>
            Dashboard
          </BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <TachometerAltIcon />
          </Icon>
          Dashboard
        </Content>
        <Content component="p">Overview of your organization&apos;s infrastructure usage and activity.</Content>
      </PageSection>

      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
    </>
  );
};

export { Dashboard };
